import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { SearchOutlined } from '@ant-design/icons'
import { Pagination, Divider, Flex } from 'antd'

import { evoClient, fetchParts } from '$api/client'
import { retrieveTokens } from '$api/tokenManagement'
import PartsListsTabs from '$components/PartsListsTabs'
import { StyledTable } from './styledComponents'
import useMaterialMapping from '$utils/useMaterialMapping'
import PartNo from './PartNo'
import FilterDropdown from '$components/FilterDropdown'
import { getDefaultColumnDefinitions, getColumnsArray } from './columns'
import DownloadPartsButton from '$components/FileDownload/DownloadPartsButton'
import { theme } from 'antd'
import Tooltip from '$components/Tooltip'
import { PartsTableContext } from './PartsTableContext'

const { useToken } = theme

const DEFAULT_FILTER_COLUMNS = ['part_id', 'name', 'material']

const getPartEventsUrl = (baseUrl, accessToken) => {
  if (!baseUrl || !accessToken) {
    return null
  }

  try {
    const normalizedBaseUrl = new URL(
      baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`,
      globalThis.location.origin,
    )
    const eventUrl = new URL('parts/events', normalizedBaseUrl)
    eventUrl.searchParams.set('access_token', accessToken)
    return eventUrl.toString()
  } catch (error) {
    console.warn('Could not create parts event stream URL', error)
    return null
  }
}

const PartsTable = ({
  columns = ['part_id', 'name'],
  columnDefinitions = {},
  expandable = {},
  filterable = {},
  selectable = false,
  groupable = false,
  comparable = false,
  groupId,
  footer,
  onLoad = () => {},
  queryEnabled = true,
}) => {
  const { t } = useTranslation()
  const { token } = useToken()
  const [searchParams] = useSearchParams()
  const [urlMaterialFilter, setUrlMaterialFilter] = useState(null)
  const { materialsById } = useMaterialMapping()

  const [selectedParts, setSelectedParts] = useState({})

  const queryClient = useQueryClient()

  useEffect(() => {
    if (!queryEnabled || typeof EventSource === 'undefined') {
      return
    }

    const { accesstoken } = retrieveTokens()
    const eventUrl = getPartEventsUrl(evoClient?.defaults?.baseURL, accesstoken)
    if (!eventUrl) {
      return
    }

    const eventSource = new EventSource(eventUrl)

    let debounceTimer = null
    eventSource.onmessage = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['parts'] })
      }, 300)
    }

    eventSource.onerror = (event) => {
      console.warn('Parts event stream connection error', event)
    }

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      eventSource.close()
    }
  }, [queryClient, queryEnabled])

  useEffect(() => {
    const materialId = searchParams.get('material_id')
    if (materialId) {
      setUrlMaterialFilter({
        client_material_id: [
          {
            parameter: 'client_material_id',
            logical_operator: '=',
            value: materialId,
          },
        ],
      })
    } else {
      setUrlMaterialFilter(null)
    }
  }, [searchParams])

  // Preprocessing of `filterable` property
  if (filterable === true) {
    // allow short-hand notation `filterable={true}` to filter default columns
    //   and ignore all other options
    filterable = {
      columns: DEFAULT_FILTER_COLUMNS,
    }
  }

  // set default values if not set
  filterable = {
    columns: [],
    filters: {},
    onChange: () => {}, // void
    ...filterable,
  }

  // Preprocessing of `selectable` property
  if (selectable) {
    // allow short-hand notation `selectable={true}` to show row selection
    //   column and ignore all other options
    selectable = {
      show: true,
      ...selectable,
    }
  } else {
    selectable = {
      show: false,
    }
  }
  selectable = {
    selectedParts,
    setSelectedParts,
    ...selectable,
  }

  // Preprocessing of `groupable` property
  if (groupable === true) {
    // allow short-hand notation `groupable={true}` to show parts lists tabs
    //   and ignore all other options
    groupable = {
      show: true,
    }
  } else if (typeof groupable === 'object') {
    if (groupable.groupId && groupId) {
      console.warn(
        "Note that PartsTable's property 'groupId' is a short-hand notation for groupable.groupId. You provided both which may lead into unexpected behavior.",
      )
    }

    groupable = {
      show: true,
      ...groupable,
    }
  }
  // set default values if not set
  groupable = {
    show: false,
    groupId,
    setGroupId: () => {},
    setGroup: () => {},
    ...groupable,
  }

  // const [loading, setLoading] = useState(true)
  const [currentParts, setCurrentParts] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [totalParts, setTotalParts] = useState()
  const [pageSize, setPageSize] = useState(10)
  const [pageNr, setPageNr] = useState(1)
  const [sorting, setSorting] = useState({})
  const [innerFilters, setInnerFilters] = useState(filterable.filters)
  const [innerGroupId, setInnerGroupId] = useState(groupable.groupId)

  const mergedFilters = useMemo(
    () => ({
      ...filterable.filters,
      ...innerFilters,
      ...(urlMaterialFilter || {}),
    }),
    [filterable.filters, innerFilters, urlMaterialFilter],
  )

  let classNames = new Set(['parts-table'])

  const queryFilters = {
    pageNr,
    pageSize,
    sorting,
    filters: mergedFilters,
    groupId: innerGroupId,
  }

  const { isFetching } = useQuery({
    queryKey: ['parts', queryFilters],
    queryFn: fetchParts,
    enabled: queryEnabled,
    onSuccess: ({
      items: parts,
      total,
      page,
      reference_part: referencePart,
    }) => {
      if (referencePart && page > 1) {
        referencePart.isPinned = true
        setCurrentParts([referencePart, ...parts])
      } else {
        setCurrentParts(parts)
      }
      setTotalParts(total)
      setSelectedRowKeys(Object.keys(selectable.selectedParts))
      onLoad(parts, total)
    },
  })

  const mergedColumnDefinitions = {
    ...getDefaultColumnDefinitions({ t, filters: innerFilters, theme: token }),
    ...columnDefinitions,
  }

  const setReferencePartId = (referencePartId) => {
    // setLoading(true)
    if (referencePartId) {
      setSorting({
        key: 'vector8',
        order: 1,
        referencePartId,
      })

      // as of now, we don't support mixing filters and sorting by vectors
      setInnerFilters({})
    } else {
      setSorting({})
    }
    setPageNr(1)
  }

  const onChange = (_pagination, tableFilters, sorter, { action }) => {
    // setLoading(true)
    if (action === 'sort') {
      if (sorter.column === undefined) {
        setSorting({})
      } else {
        setSorting({
          key:
            sorter.field ||
            sorter.column?.field ||
            sorter.column?.title ||
            undefined,
          order: sorter.order === 'ascend' ? 1 : -1,
        })
      }
      setPageNr(1)
    } else if (action === 'filter') {
      // as of now, we don't support mixing filters and sorting by vectors
      if (sorting.key === 'vector8') {
        setSorting({})
      }

      let newFilters = {}
      for (const [column, selectedKeys] of Object.entries(tableFilters)) {
        if (selectedKeys) {
          const [{ searchTerm, searchOperator }, ..._otherFilters] =
            selectedKeys
          if (typeof newFilters[column] !== 'object') {
            newFilters[column] = []
          }
          newFilters[column].push({
            parameter:
              mergedColumnDefinitions[column]?.filter?.parameter ?? column,
            logical_operator: searchOperator ?? 'contains',
            value: searchTerm,
          })
        }
      }
      setInnerFilters(newFilters)
      filterable.onChange(newFilters)
      setPageNr(1)
    }
  }

  let rowSelection
  if (selectable.show === true) {
    // If `selectable.show` is true,
    //   (i) we'll add a column to select parts,
    //   (ii) we'll show a footer that states the number of selected parts

    const onSelectMultiple = (selected, _selectedRows, changeRows) => {
      if (selected) {
        changeRows.forEach((part) => {
          selectable.selectedParts[part.id] = part
        })
      } else {
        changeRows.forEach((part) => {
          delete selectable.selectedParts[part.id]
        })
      }
      selectable.setSelectedParts(selectable.selectedParts)
    }

    rowSelection = {
      selectedRowKeys,
      onSelect: (part, selected) => {
        if (selected) {
          selectable.selectedParts[part.id] = part
        } else {
          delete selectable.selectedParts[part.id]
        }
        selectable.setSelectedParts(selectable.selectedParts)
      },
      onSelectMultiple: onSelectMultiple,
      onSelectAll: onSelectMultiple,
      onChange: (_selectedRowKeys, selectedRows) => {
        selectedRows.forEach((part) => {
          selectable.selectedParts[part.id] = part
        })
        selectable.setSelectedParts({ ...selectable.selectedParts }) // Update the state with a new object, ensuring rerendering of selectedParts
        setSelectedRowKeys(Object.keys(selectable.selectedParts))
      },
    }

    // If none is provided via props, add a footer that shows the
    //   number of selected parts.
    if (typeof footer !== 'function') {
      footer = () => {
        const size = Object.keys(selectable.selectedParts).length
        if (size === 0) {
          return ''
        }
        return <i>{t('parts_selected', { count: size })}</i>
      }
    }
  }

  let expandableDefinition = {}
  if (typeof expandable.expandedRowRender === 'function') {
    // If `expandable` is passed to the component with a `expandedRowRender`, it
    //   is used for row expansion.
    // By default, this adds the ExpandColumn at the very beginning of the table.
    classNames.add('expandable')

    const onExpand = (expanded, record) => {
      if (expanded) {
        setExpandedRowKeys([...expandedRowKeys, record.id])
      } else {
        setExpandedRowKeys(
          expandedRowKeys.filter((rowKey) => rowKey !== record.id),
        )
      }
    }

    expandableDefinition = {
      expandedRowKeys,
      onExpand,
      showExpandColumn: undefined,
      expandRowByClick: false,
      ...expandable,
    }

    // If `selectedParts` is passed to the component as well and thus a rowSelection
    //   is set-up, we avoid having both the ExpandColumn and SelectColumn at the
    //   table's beginning and use either the column 'Part No' or 'Part Name' as
    //   expander column by default.
    if (
      rowSelection &&
      columns.includes('part_id') &&
      typeof columnDefinitions?.part_id?.render === 'undefined' &&
      expandable.showExpandColumn !== true
    ) {
      if (typeof expandableDefinition.showExpandColumn === 'undefined') {
        // override only if not previously set via the `expandable` parameter
        expandableDefinition.showExpandColumn = false
      }

      // use different, interactive renderer for `part_id` column
      mergedColumnDefinitions.part_id.render = (_id, part) => (
        <PartNo
          part={part}
          expandedRowKeys={expandedRowKeys}
          onExpand={onExpand}
        />
      )
    }
  }

  for (const column of filterable.columns) {
    mergedColumnDefinitions[column].filterDropdown = ({
      confirm,
      clearFilters,
      setSelectedKeys,
    }) => (
      <FilterDropdown
        confirm={confirm}
        clearFilters={clearFilters}
        setSelectedKeys={setSelectedKeys}
        operators={
          mergedColumnDefinitions[column].filter?.operators ?? [
            mergedColumnDefinitions[column].filter?.operator ?? '=',
          ]
        }
        parseValue={
          mergedColumnDefinitions[column].filter?.parse ?? ((id) => id)
        }
      />
    )
    mergedColumnDefinitions[column].filterIcon = (filtered) => (
      <Tooltip title={t('marketplace.search_icon_part_name')}>
        <SearchOutlined
          style={{ color: filtered ? '#1890ff' : token.colorPrimary }}
        />
      </Tooltip>
    )
  }

  const on3DModelDelete = (partId) => {
    if (sorting?.referencePartId === partId) {
      setReferencePartId(null)
    } else {
      queryClient.invalidateQueries({ queryKey: ['parts'] })
    }
  }

  const contextValue = useMemo(() => ({ on3DModelDelete }), [on3DModelDelete])

  return (
    <PartsTableContext.Provider value={contextValue}>
      {groupable.show && (
        <PartsListsTabs
          groupId={innerGroupId}
          setGroupId={(groupId) => {
            setInnerGroupId(groupId)
            groupable.setGroupId(groupId)
            setPageNr(1)
          }}
          setGroup={groupable.setGroup}
        />
      )}
      <StyledTable
        className={[...classNames].join(' ')}
        loading={isFetching}
        size="middle"
        expandable={expandableDefinition}
        columns={getColumnsArray(
          t,
          columns,
          mergedColumnDefinitions,
          sorting,
          selectable.show,
          comparable ? setReferencePartId : undefined,
          sorting.referencePartId,
          materialsById,
        )}
        rowKey={(part) => part.id}
        dataSource={currentParts}
        pagination={false}
        onChange={onChange}
        rowSelection={rowSelection}
        data-page={pageNr}
        footer={() => (
          <Flex
            justify="space-between"
            align="center"
            style={{ width: '100%' }}
          >
            {/* render custom footer */}
            <div>{footer ? footer(selectable.selectedParts) : undefined}</div>
            {/* render fixed footer content */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <DownloadPartsButton size="small" queryFilters={queryFilters} />
              <Divider type="vertical" style={{ height: '16px' }} />
              <Pagination
                size="small"
                current={pageNr}
                pageSize={pageSize}
                total={totalParts}
                onChange={(page, pageSize) => {
                  // setLoading(true)
                  setPageNr(page)
                  setPageSize(pageSize)
                }}
              />
            </div>
          </Flex>
        )}
      />
    </PartsTableContext.Provider>
  )
}

export default PartsTable
